/**
 * Wingman Spatial Replay System
 * 
 * PURPOSE: Record and replay spatial sequences to ensure deterministic behavior.
 * Used for regression testing and forensic analysis.
 */

export interface ReplayFrame {
    timestamp: number;
    transcript: string;
    expectedCenter: { lat: number; lon: number };
    expectedZoom: number;
}

export interface ReplaySession {
    id: string;
    name: string;
    frames: ReplayFrame[];
}

export class SpatialReplaySystem {
    private isRecording: boolean = false;
    private currentSession: ReplaySession | null = null;

    /**
     * Start recording a new session
     */
    startRecording(name: string) {
        console.info(`[REPLAY] ⏺️ Starting recording: ${name}`);
        this.isRecording = true;
        this.currentSession = {
            id: `session_${Date.now()}`,
            name,
            frames: []
        };
    }

    /**
     * Record a frame when a movement is triggered
     */
    recordFrame(transcript: string, center: { lat: number; lon: number }, zoom: number) {
        if (!this.isRecording || !this.currentSession) return;

        this.currentSession.frames.push({
            timestamp: Date.now(),
            transcript,
            expectedCenter: center,
            expectedZoom: zoom
        });
    }

    /**
     * Stop recording and return the session JSON
     */
    stopRecording(): ReplaySession | null {
        console.info(`[REPLAY] ⏹️ Recording stopped.`);
        this.isRecording = false;
        return this.currentSession;
    }

    /**
     * Replay a session and verify accuracy
     */
    async replay(session: ReplaySession, engine: any): Promise<{ success: boolean; errors: string[] }> {
        console.info(`[REPLAY] ▶️ Replaying session: ${session.name}`);
        const errors: string[] = [];

        for (const frame of session.frames) {
            console.info(`[REPLAY] Executing: "${frame.transcript}"`);
            
            // 1. Inject the message
            await engine.processUserMessage(frame.transcript);

            // 2. Wait for settlement (telemetry verification)
            await new Promise(resolve => setTimeout(resolve, 5000));

            const current = (window as any).__MAP_CAMERA__;
            if (!current) {
                errors.push(`Frame "${frame.transcript}": No camera telemetry found.`);
                continue;
            }

            // 3. Accuracy Assertion (100m tolerance)
            const dist = Math.sqrt(
                Math.pow(current.center.lat - frame.expectedCenter.lat, 2) + 
                Math.pow(current.center.lng - frame.expectedCenter.lon, 2)
            );

            if (dist > 0.005) {
                errors.push(`Frame "${frame.transcript}": Accuracy mismatch. Delta=${dist.toFixed(5)}`);
            } else {
                console.info(`[REPLAY] ✅ Frame verified within tolerance.`);
            }
        }

        return {
            success: errors.length === 0,
            errors
        };
    }
}

export const replaySystem = new SpatialReplaySystem();

// Expose to window for console/test access
if (typeof window !== 'undefined') {
    (window as any).WingmanReplay = replaySystem;
}
